import './styles.css';
import {
  GameState,
  InputBits,
  createGameState,
  destroyEnemy,
  isLeaderboardEligible,
  playerDisplayScore,
  setPaused,
  startTractorBeam,
  step,
} from './shared/simulation';
import {
  createRoundDescriptor,
  GameMode,
  RULESET_ID,
  stageBadgeDenominations,
} from './shared/ruleset';
import { InputEvent, normalizeDisplayName } from './shared/protocol';

const WORKER_URL = import.meta.env.VITE_WORKER_URL || import.meta.env.VITE_LOCAL_WORKER_URL || '';
const STORAGE_KEY = 'starfall-preferences-v1';
const HIGH_SCORE_KEY = 'starfall-high-scores-v1';

interface Preferences {
  muted: boolean;
  volume: number;
  reducedMotion: boolean;
}

interface LocalScore {
  name: string;
  score: number;
  stage: number;
  createdAt: string;
}

interface OnlineScore {
  rank: number;
  displayName: string;
  score: number;
  stage: number;
  mode: GameMode;
  playerSlot: 1 | 2;
  createdAt: string;
}

type BrowserTestScenario = 'boss' | 'capture' | 'formation-captor' | 'dual' | 'morph' | 'challenge' | 'stage-clear' | 'game-over';

interface BrowserTestBridge {
  reset(seed?: number, mode?: GameMode): void;
  scenario(name: BrowserTestScenario): void;
  step(inputBits?: number, ticks?: number): void;
  completeChallenge(): void;
  spawnEnemyShot(): void;
  state(): { seed: number; tick: number; phase: string; round: number; score: number; lives: number; fighterCount: number; capture: string; playerX: number; shots: number; enemyShots: number; explosions: number; enemyCount: number; challengeDestroyed: number; bossDamaged: boolean } | null;
}

declare global {
  interface Window {
    __STARFALL_TEST__?: BrowserTestBridge;
  }
}

const defaultPreferences: Preferences = { muted: false, volume: 0.45, reducedMotion: false };

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character] ?? character);
}

class SynthAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private preferences: Preferences;
  private musicTimer: number | null = null;

  constructor(preferences: Preferences) {
    this.preferences = preferences;
  }

  activate(): void {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.connect(this.context.destination);
    }
    if (this.context.state === 'suspended') void this.context.resume();
    this.updateVolume();
  }

  updateVolume(): void {
    if (this.master) this.master.gain.value = this.preferences.muted ? 0 : this.preferences.volume;
  }

  cue(name: string): void {
    if (this.preferences.muted || !this.context || !this.master) return;
    const patterns: Record<string, [number, number, number]> = {
      fire: [620, 0.04, 0.03],
      'enemy-hit': [210, 0.07, 0.04],
      'boss-damage': [130, 0.16, 0.08],
      'enemy-destroyed': [320, 0.12, 0.06],
      'tractor-beam': [85, 0.36, 0.05],
      capture: [60, 0.3, 0.04],
      rescue: [740, 0.3, 0.08],
      'fighter-lost': [110, 0.25, 0.05],
      'extra-life': [900, 0.28, 0.1],
      morph: [430, 0.2, 0.07],
      'charged-cannon': [48, 0.5, 0.07],
      'challenge-perfect': [980, 0.65, 0.08],
      'stage-clear': [520, 0.45, 0.07],
      'stage-start': [260, 0.3, 0.06],
      'challenging-stage': [680, 0.45, 0.08],
      'turn-change': [390, 0.25, 0.06],
      'game-over': [75, 0.65, 0.06],
      title: [330, 0.2, 0.04],
      'high-score': [760, 0.12, 0.05],
    };
    const [frequency, duration, gain] = patterns[name] ?? patterns['enemy-hit']!;
    const oscillator = this.context.createOscillator();
    const envelope = this.context.createGain();
    oscillator.type = name === 'fire' ? 'square' : 'sawtooth';
    oscillator.frequency.setValueAtTime(frequency, this.context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(40, frequency * 0.45), this.context.currentTime + duration);
    envelope.gain.setValueAtTime(gain, this.context.currentTime);
    envelope.gain.exponentialRampToValueAtTime(0.001, this.context.currentTime + duration);
    oscillator.connect(envelope).connect(this.master);
    oscillator.start();
    oscillator.stop(this.context.currentTime + duration + 0.02);
  }

  startAttractMusic(): void {
    this.activate();
    if (this.musicTimer !== null) return;
    const notes = ['title', 'title', 'stage-start', 'title'];
    let index = 0;
    this.musicTimer = window.setInterval(() => this.cue(notes[index++ % notes.length] ?? 'title'), 420);
  }

  stopAttractMusic(): void {
    if (this.musicTimer !== null) window.clearInterval(this.musicTimer);
    this.musicTimer = null;
  }
}

class GameApp {
  private root: HTMLElement;
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private state: GameState | null = null;
  private demoState: GameState | null = null;
  private mode: GameMode = 'single-player';
  private preferences: Preferences = { ...defaultPreferences };
  private audio: SynthAudio;
  private lastTime = 0;
  private accumulator = 0;
  private attractTick = 0;
  private onlineEligible = false;
  private statusMessage = 'READY FOR INSERT COIN';
  private highScores: LocalScore[] = [];
  private panel: HTMLElement;
  private status: HTMLElement;
  private scoreEl: HTMLElement;
  private playerEl: HTMLElement;
  private stageEl: HTMLElement;
  private livesEl: HTMLElement;
  private onlineEl: HTMLElement;
  private announcement: HTMLElement;
  private keys = new Set<string>();
  private dragX: number | null = null;
  private fireHeld = false;
  private onlineRunId: string | null = null;
  private onlineTokens: Partial<Record<1 | 2, string>> = {};
  private onlineSequences: { 1: number; 2: number } = { 1: 0, 2: 0 };
  private pendingOnlineEvents: { 1: InputEvent[]; 2: InputEvent[] } = { 1: [], 2: [] };
  private onlineUploading: { 1: boolean; 2: boolean } = { 1: false, 2: false };
  private pendingTransition: 'pause' | 'resume' | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.preferences = { ...defaultPreferences, ...readJson<Partial<Preferences>>(STORAGE_KEY, {}) };
    this.highScores = readJson<LocalScore[]>(HIGH_SCORE_KEY, []).slice(0, 10);
    this.audio = new SynthAudio(this.preferences);
    this.root.innerHTML = `
      <div class="app-shell">
        <header class="topbar">
          <div class="brand"><span class="brand-mark">✦</span><div><strong>STARFALL</strong><small>GALAGA ARCADE // REV. B</small></div></div>
          <div class="connection"><span class="status-dot"></span><span id="online-status">LOCAL READY</span></div>
          <label class="volume-control" for="volume-control">VOL <input id="volume-control" type="range" min="0" max="1" step="0.05" value="0.45" aria-label="Volume" /></label><button class="icon-button" id="sound-button" type="button" aria-label="Toggle sound">SOUND ON</button>
        </header>
        <section class="cabinet" aria-label="Game cabinet">
          <div class="screen-bezel">
            <div class="screen-header"><span id="player-label">1UP</span><span id="score-label">000000</span><span id="stage-label">STAGE 1</span></div>
            <div class="canvas-wrap"><canvas id="game-canvas" width="288" height="384" tabindex="0" aria-label="Starfall game screen"></canvas><div class="scanlines"></div></div>
            <div class="screen-footer"><span id="lives-label">FIGHTERS 03</span><span class="rule-chip">NAMCO REV. B</span><span id="connection-footer">OFFLINE OK</span></div>
          </div>
          <aside class="side-panel">
            <div class="eyebrow">MISSION STATUS</div><h1 id="status-message">READY FOR<br><em>INSERT COIN</em></h1>
            <p class="status-copy">Defend the formation. Rescue captured fighters. Reach the kill screen at stage 255.</p>
            <div class="badge-readout"><span class="eyebrow">STAGE BADGE</span><div id="badge-display" class="badges"></div></div>
            <div class="meter"><div><span>RUN MODE</span><strong id="mode-display">SINGLE PLAYER</strong></div><div><span>RULESET</span><strong>namco-rev-b-v1</strong></div></div>
          </aside>
        </section>
        <nav class="control-dock" aria-label="Game controls">
          <div class="touch-pad" id="touch-pad" role="slider" aria-label="Horizontal fighter control" aria-valuemin="0" aria-valuemax="100" aria-valuenow="50" tabindex="0"><span class="pad-track"></span><span class="pad-knob"></span><span class="pad-label">DRAG TO PILOT</span></div>
          <button class="fire-button" id="fire-button" type="button" aria-label="Fire">FIRE<span>SPACE</span></button>
          <div class="action-buttons"><button id="pause-button" type="button">PAUSE <kbd>P</kbd></button><button id="start-button" type="button" class="primary">INSERT COIN <kbd>ENTER</kbd></button></div>
        </nav>
        <section class="below-fold"><div class="quick-links"><button id="instructions-button" type="button">HOW TO PLAY</button><button id="scores-button" type="button">LOCAL SCORES</button><button id="leaderboard-button" type="button">NETWORK BOARD</button></div><p class="keyboard-hint">ARROW KEYS / A D TO MOVE <span>•</span> SPACE TO FIRE <span>•</span> P TO PAUSE</p></section>
        <div id="game-panel" class="modal-layer" hidden></div>
        <div id="announcement" class="sr-only" aria-live="polite"></div>
      </div>`;
    this.canvas = this.root.querySelector<HTMLCanvasElement>('#game-canvas')!;
    this.context = this.canvas.getContext('2d')!;
    this.resizeCanvas();
    this.panel = this.root.querySelector<HTMLElement>('#game-panel')!;
    this.status = this.root.querySelector<HTMLElement>('#status-message')!;
    this.scoreEl = this.root.querySelector<HTMLElement>('#score-label')!;
    this.playerEl = this.root.querySelector<HTMLElement>('#player-label')!;
    this.stageEl = this.root.querySelector<HTMLElement>('#stage-label')!;
    this.livesEl = this.root.querySelector<HTMLElement>('#lives-label')!;
    this.onlineEl = this.root.querySelector<HTMLElement>('#online-status')!;
    this.announcement = this.root.querySelector<HTMLElement>('#announcement')!;
    this.root.querySelector<HTMLInputElement>('#volume-control')!.value = String(this.preferences.volume);
    this.root.querySelector<HTMLButtonElement>('#sound-button')!.textContent = this.preferences.muted ? 'SOUND OFF' : 'SOUND ON';
    this.bindControls();
    window.addEventListener('resize', () => this.resizeCanvas());
    this.setOnlineStatus(navigator.onLine);
    window.addEventListener('online', () => this.setOnlineStatus(true));
    window.addEventListener('offline', () => this.setOnlineStatus(false));
    if ('serviceWorker' in navigator) void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
    this.render(0);
    requestAnimationFrame((time) => this.loop(time));
  }

  private bindControls(): void {
    this.root.querySelector<HTMLButtonElement>('#start-button')!.addEventListener('click', () => this.openModePanel());
    this.root.querySelector<HTMLButtonElement>('#pause-button')!.addEventListener('click', () => this.togglePause());
    this.root.querySelector<HTMLButtonElement>('#sound-button')!.addEventListener('click', () => this.toggleSound());
    this.root.querySelector<HTMLInputElement>('#volume-control')!.addEventListener('input', (event) => { this.preferences.volume = Number((event.target as HTMLInputElement).value); this.audio.updateVolume(); this.persistPreferences(); });
    this.root.querySelector<HTMLButtonElement>('#instructions-button')!.addEventListener('click', () => this.openInstructions());
    this.root.querySelector<HTMLButtonElement>('#scores-button')!.addEventListener('click', () => this.openScores());
    this.root.querySelector<HTMLButtonElement>('#leaderboard-button')!.addEventListener('click', () => void this.openLeaderboard());
    const fire = this.root.querySelector<HTMLButtonElement>('#fire-button')!;
    fire.addEventListener('pointerdown', (event) => { event.preventDefault(); this.fireHeld = true; this.audio.activate(); });
    fire.addEventListener('pointerup', () => { this.fireHeld = false; });
    fire.addEventListener('pointercancel', () => { this.fireHeld = false; });
    fire.addEventListener('pointerleave', () => { this.fireHeld = false; });
    const pad = this.root.querySelector<HTMLElement>('#touch-pad')!;
    pad.addEventListener('pointerdown', (event) => { event.preventDefault(); pad.setPointerCapture(event.pointerId); this.dragX = event.clientX; this.audio.activate(); this.updatePad(event.clientX); });
    pad.addEventListener('pointermove', (event) => { if (this.dragX !== null) this.updatePad(event.clientX); });
    const release = () => { this.dragX = null; this.updatePad(null); };
    pad.addEventListener('pointerup', release);
    pad.addEventListener('pointercancel', release);
    pad.addEventListener('keydown', (event) => { if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.dragX = event.key === 'ArrowLeft' ? 0 : 10_000; } });
    window.addEventListener('keydown', (event) => this.onKey(event, true));
    window.addEventListener('keyup', (event) => this.onKey(event, false));
    document.addEventListener('visibilitychange', () => { if (document.hidden && this.state?.phase === 'playing') this.pauseForVisibility(); });
  }

  private resizeCanvas(): void {
    const pixelRatio = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    const width = Math.round(288 * pixelRatio);
    const height = Math.round(384 * pixelRatio);
    if (this.canvas.width === width && this.canvas.height === height) {
      this.context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      this.context.imageSmoothingEnabled = false;
      return;
    }
    this.canvas.width = width;
    this.canvas.height = height;
    this.context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    this.context.imageSmoothingEnabled = false;
  }

  private onKey(event: KeyboardEvent, down: boolean): void {
    if (['ArrowLeft', 'ArrowRight', 'a', 'd', 'A', 'D', ' ', 'p', 'P', 'Enter'].includes(event.key)) event.preventDefault();
    if (down) {
      if (event.key === 'Enter' && !this.state) this.openModePanel();
      if ((event.key === 'p' || event.key === 'P') && !event.repeat) this.togglePause();
      if (event.key === ' ') { this.fireHeld = true; this.audio.activate(); }
      this.keys.add(event.key);
    } else {
      this.keys.delete(event.key);
      if (event.key === ' ') this.fireHeld = false;
    }
  }

  private updatePad(clientX: number | null): void {
    const pad = this.root.querySelector<HTMLElement>('#touch-pad')!;
    const knob = pad.querySelector<HTMLElement>('.pad-knob')!;
    if (clientX === null) { knob.style.transform = 'translateX(-50%)'; pad.setAttribute('aria-valuenow', '50'); return; }
    const box = pad.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((clientX - box.left) / box.width) * 100));
    knob.style.transform = `translateX(calc(${percent - 50}%))`;
    pad.setAttribute('aria-valuenow', String(Math.round(percent)));
  }

  private input(): number {
    let bits = InputBits.NONE;
    if (this.keys.has('ArrowLeft') || this.keys.has('a') || this.keys.has('A') || (this.dragX !== null && this.dragX < 0)) bits |= InputBits.LEFT;
    if (this.keys.has('ArrowRight') || this.keys.has('d') || this.keys.has('D') || (this.dragX !== null && this.dragX > 0)) bits |= InputBits.RIGHT;
    if (this.fireHeld || this.keys.has(' ')) bits |= InputBits.FIRE;
    const gamepad = [...(navigator.getGamepads?.() ?? [])].find((candidate) => candidate?.connected);
    if (gamepad) {
      const axis = gamepad.axes[0] ?? 0;
      if (axis < -0.25) bits |= InputBits.LEFT;
      if (axis > 0.25) bits |= InputBits.RIGHT;
      if (gamepad.buttons[0]?.pressed) bits |= InputBits.FIRE;
    }
    if (this.dragX !== null && this.dragX >= 0) {
      const box = this.root.querySelector<HTMLElement>('#touch-pad')!.getBoundingClientRect();
      const midpoint = box.left + box.width / 2;
      bits &= ~(InputBits.LEFT | InputBits.RIGHT);
      if (this.dragX < midpoint - 8) bits |= InputBits.LEFT;
      if (this.dragX > midpoint + 8) bits |= InputBits.RIGHT;
    }
    return bits;
  }

  private loop(time: number): void {
    if (!this.lastTime) this.lastTime = time;
    const elapsed = import.meta.env.DEV ? time - this.lastTime : Math.min(100, time - this.lastTime);
    this.lastTime = time;
    this.accumulator += elapsed;
    const frame = 1000 / 60;
    const maxSimulationSteps = import.meta.env.DEV ? 240 : 5;
    let steps = 0;
    while (this.accumulator >= frame && steps < maxSimulationSteps) {
      this.tick();
      this.accumulator -= frame;
      steps++;
    }
    this.render(time);
    requestAnimationFrame((next) => this.loop(next));
  }

  private tick(): void {
    if (!this.state) {
      this.attractTick++;
      if (this.attractTick > 180) {
        this.demoState ??= createGameState(0x1981, 'single-player');
        const direction = Math.sin(this.attractTick / 55) > 0 ? InputBits.RIGHT : InputBits.LEFT;
        step(this.demoState, direction | (this.attractTick % 18 === 0 ? InputBits.FIRE : InputBits.NONE));
      }
      return;
    }
    const before = this.state.phase;
    const slot = this.state.activePlayer;
    const transition = this.pendingTransition;
    this.pendingTransition = null;
    const inputBits = transition ? InputBits.NONE : this.input();
    step(this.state, inputBits);
    if (transition) this.queueOnlineEvent(slot, InputBits.NONE, transition, this.state.tick);
    else if (before !== 'paused' && this.state.phase !== 'paused') this.queueOnlineEvent(slot, inputBits, 'none', this.state.tick);
    for (const event of this.state.events) if (event.cue) this.audio.cue(event.cue);
    if (before !== this.state.phase || this.state.events.some((event) => event.type === 'turn-change')) this.announceState();
    if (this.state.phase === 'game-over' && before !== 'game-over') this.openGameOver();
    if (this.state.phase === 'arcade-boundary' && before !== 'arcade-boundary') { this.onlineEligible = false; this.openArcadeBoundary(); }
  }

  private render(time: number): void {
    const ctx = this.context;
    ctx.clearRect(0, 0, 288, 384);
    ctx.fillStyle = '#07091a';
    ctx.fillRect(0, 0, 288, 384);
    this.drawStars(ctx, time);
    if (this.state) this.drawGame(ctx, this.state);
    else if (this.demoState) {
      this.drawGame(ctx, this.demoState);
      this.drawCenterMessage(ctx, 'DEMO PLAY', 'INSERT COIN TO LAUNCH');
    }
    else this.drawAttract(ctx);
    this.updateHud();
  }

  private drawStars(ctx: CanvasRenderingContext2D, time: number): void {
    for (let index = 0; index < 48; index++) {
      const x = (index * 73 + 17) % 288;
      const y = (index * 47 + Math.floor(time / 80) * (index % 3 + 1)) % 384;
      const alpha = 0.25 + (index % 4) * 0.12;
      ctx.fillStyle = `rgba(137, 219, 255, ${alpha})`;
      ctx.fillRect(x, y, index % 5 === 0 ? 2 : 1, index % 5 === 0 ? 2 : 1);
    }
  }

  private drawAttract(ctx: CanvasRenderingContext2D): void {
    ctx.textAlign = 'center';
    ctx.font = 'bold 25px monospace';
    ctx.fillStyle = '#ff3f70';
    ctx.fillText('STARFALL', 144, 86);
    ctx.font = 'bold 9px monospace';
    ctx.fillStyle = '#79eaff';
    ctx.fillText('A GALAGA-STYLE ARCADE', 144, 104);
    this.drawFormationPreview(ctx, this.attractTick);
    this.drawFighter(ctx, 144, 334, false, false);
    ctx.font = 'bold 10px monospace';
    ctx.fillStyle = '#f7d66d';
    ctx.fillText('ENTER / INSERT COIN', 144, 270 + Math.sin(this.attractTick / 16) * 2);
    ctx.fillStyle = '#6c779d';
    ctx.font = '8px monospace';
    ctx.fillText('TOUCH CONTROLS AVAILABLE', 144, 286);
  }

  private drawFormationPreview(ctx: CanvasRenderingContext2D, tick: number): void {
    const kinds: Array<'boss' | 'butterfly' | 'bee'> = ['boss', 'boss', 'boss', 'boss', 'butterfly', 'butterfly', 'butterfly', 'butterfly', 'bee', 'bee', 'bee', 'bee'];
    kinds.forEach((kind, index) => this.drawEnemy(ctx, kind, 72 + (index % 4) * 28, 140 + Math.floor(index / 4) * 26, index === 0 && tick % 100 < 50));
  }

  private drawGame(ctx: CanvasRenderingContext2D, state: GameState): void {
    for (const enemy of state.enemies) {
      if (enemy.phase === 'destroyed') continue;
      const x = enemy.kind === 'challenge' ? enemy.x : enemy.x;
      const y = enemy.y;
      if (enemy.kind === 'morph') this.drawMorph(ctx, x, y, enemy.morphKind ?? 'scorpion');
      else if (enemy.kind === 'challenge') this.drawChallenge(ctx, x, y, enemy.challengeTarget ?? 'dragonfly');
      else this.drawEnemy(ctx, enemy.kind, x, y, enemy.damaged);
      if ((state.players[state.activePlayer].capture === 'pulling' || state.players[state.activePlayer].capture === 'enemy-controlled') && enemy.kind === 'boss' && (enemy.phase === 'attack' || enemy.phase === 'formation')) this.drawBeam(ctx, enemy.x, enemy.y, state.playerX);
    }
    for (const shot of state.shots) this.drawShot(ctx, shot.x, shot.y, '#f7f3bb');
    for (const shot of state.enemyShots) this.drawShot(ctx, shot.x, shot.y, shot.charged ? '#f7d66d' : '#ff507b');
    for (const explosion of state.explosions) this.drawExplosion(ctx, explosion.x, explosion.y, explosion.age, explosion.color);
    const current = state.players[state.activePlayer];
    this.drawFighter(ctx, state.playerX, 356, current.fighterCount === 2, false);
    if (current.capture === 'pulling' || current.capture === 'enemy-controlled') {
      const captor = state.enemies.find((enemy) => enemy.kind === 'boss' && enemy.phase !== 'destroyed');
      if (captor) this.drawOneFighter(ctx, captor.x, captor.y + 15, '#ff456f');
    }
    if (current.capture === 'returning' && state.captiveAttackTicks > 0) {
      this.drawOneFighter(ctx, state.playerX + Math.sin(state.captiveAttackTicks / 8) * 44, 275 + state.captiveAttackTicks, '#ff456f');
    }
    if (state.phase === 'stage-clear') this.drawCenterMessage(ctx, `STAGE ${state.round} CLEAR`, 'FORMATION DISPERSED');
    if (state.phase === 'paused') this.drawCenterMessage(ctx, 'PAUSED', 'PRESS P TO RESUME');
    if (state.phase === 'arcade-boundary') this.drawCenterMessage(ctx, 'STAGE 0', 'ARCADE BOUNDARY // LOCAL RUN ENDED');
  }

  private drawEnemy(ctx: CanvasRenderingContext2D, kind: 'bee' | 'butterfly' | 'boss', x: number, y: number, damaged = false): void {
    ctx.save(); ctx.translate(x, y); ctx.fillStyle = kind === 'bee' ? '#69e6ff' : kind === 'butterfly' ? '#d88cff' : damaged ? '#fff3a1' : '#ff527a'; ctx.strokeStyle = '#1b214d'; ctx.lineWidth = 1;
    ctx.beginPath();
    if (kind === 'bee') { ctx.moveTo(0, -8); ctx.lineTo(7, -3); ctx.lineTo(6, 5); ctx.lineTo(2, 8); ctx.lineTo(-2, 8); ctx.lineTo(-6, 5); ctx.lineTo(-7, -3); ctx.closePath(); }
    else if (kind === 'butterfly') { ctx.moveTo(0, -8); ctx.lineTo(5, -5); ctx.lineTo(10, 3); ctx.lineTo(4, 5); ctx.lineTo(0, 2); ctx.lineTo(-4, 5); ctx.lineTo(-10, 3); ctx.lineTo(-5, -5); ctx.closePath(); }
    else { ctx.moveTo(0, -10); ctx.lineTo(9, -4); ctx.lineTo(11, 5); ctx.lineTo(4, 9); ctx.lineTo(-4, 9); ctx.lineTo(-11, 5); ctx.lineTo(-9, -4); ctx.closePath(); }
    ctx.fill(); ctx.stroke(); ctx.fillStyle = '#fff'; ctx.fillRect(-2, -2, 2, 2); ctx.fillRect(1, -2, 2, 2); ctx.restore();
  }

  private drawMorph(ctx: CanvasRenderingContext2D, x: number, y: number, kind: string): void {
    const colors: Record<string, string> = { scorpion: '#f7d66d', bosconian: '#ff8f5c', flagship: '#a8ff83' };
    ctx.save(); ctx.translate(x, y); ctx.fillStyle = colors[kind] ?? '#f7d66d'; ctx.strokeStyle = '#1b214d'; ctx.beginPath(); ctx.moveTo(0, -9); ctx.lineTo(11, 3); ctx.lineTo(5, 7); ctx.lineTo(0, 4); ctx.lineTo(-5, 7); ctx.lineTo(-11, 3); ctx.closePath(); ctx.fill(); ctx.stroke(); ctx.restore();
  }

  private drawChallenge(ctx: CanvasRenderingContext2D, x: number, y: number, kind: string): void {
    const colors: Record<string, string> = { dragonfly: '#69e6ff', satellite: '#f7d66d', starship: '#ff527a' };
    ctx.save(); ctx.translate(x, y); ctx.fillStyle = colors[kind] ?? '#69e6ff'; ctx.strokeStyle = '#fff'; ctx.lineWidth = 1;
    if (kind === 'dragonfly') { ctx.fillRect(-2, -8, 4, 16); ctx.fillRect(-10, -2, 20, 4); }
    else if (kind === 'satellite') { ctx.fillRect(-4, -4, 8, 8); ctx.fillRect(-12, -2, 6, 4); ctx.fillRect(6, -2, 6, 4); }
    else { ctx.beginPath(); ctx.moveTo(0, -9); ctx.lineTo(9, 6); ctx.lineTo(-9, 6); ctx.closePath(); ctx.fill(); ctx.stroke(); }
    ctx.restore();
  }

  private drawFighter(ctx: CanvasRenderingContext2D, x: number, y: number, dual: boolean, enemyControlled: boolean): void {
    const color = enemyControlled ? '#ff456f' : '#f7f3bb';
    this.drawOneFighter(ctx, x, y, color);
    if (dual) { this.drawOneFighter(ctx, x - 12, y + 2, color); this.drawOneFighter(ctx, x + 12, y + 2, color); }
  }

  private drawOneFighter(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
    ctx.save(); ctx.translate(x, y); ctx.fillStyle = color; ctx.strokeStyle = '#69e6ff'; ctx.beginPath(); ctx.moveTo(0, -12); ctx.lineTo(5, -2); ctx.lineTo(13, 8); ctx.lineTo(4, 7); ctx.lineTo(0, 12); ctx.lineTo(-4, 7); ctx.lineTo(-13, 8); ctx.lineTo(-5, -2); ctx.closePath(); ctx.fill(); ctx.stroke(); ctx.fillStyle = '#ff527a'; ctx.fillRect(-2, 0, 4, 5); ctx.restore();
  }

  private drawShot(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void { ctx.fillStyle = color; ctx.fillRect(x - 1, y - 5, 2, 8); }
  private drawExplosion(ctx: CanvasRenderingContext2D, x: number, y: number, age: number, kind: 'enemy' | 'fighter' | 'bonus'): void {
    const colors = { enemy: '#ff527a', fighter: '#f7f3bb', bonus: '#f7d66d' };
    ctx.save(); ctx.globalAlpha = Math.max(0, 1 - age / 18); ctx.strokeStyle = colors[kind]; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(x, y, 3 + age * 1.2, 0, Math.PI * 2); ctx.stroke(); ctx.restore();
  }
  private drawBeam(ctx: CanvasRenderingContext2D, x: number, y: number, playerX: number): void { ctx.save(); ctx.globalAlpha = 0.32; ctx.fillStyle = '#b886ff'; ctx.beginPath(); ctx.moveTo(x - 6, y + 5); ctx.lineTo(x + 6, y + 5); ctx.lineTo(playerX, 350); ctx.lineTo(playerX - 8, 350); ctx.closePath(); ctx.fill(); ctx.restore(); }
  private drawCenterMessage(ctx: CanvasRenderingContext2D, title: string, subtitle: string): void { ctx.fillStyle = 'rgba(7,9,26,.86)'; ctx.fillRect(20, 158, 248, 68); ctx.textAlign = 'center'; ctx.fillStyle = '#f7d66d'; ctx.font = 'bold 12px monospace'; ctx.fillText(title, 144, 186); ctx.fillStyle = '#79eaff'; ctx.font = '8px monospace'; ctx.fillText(subtitle, 144, 205); }

  private updateHud(): void {
    const state = this.state ?? this.demoState;
    const score = state ? playerDisplayScore(state, state.activePlayer) : 0;
    this.scoreEl.textContent = String(score).padStart(state?.activePlayer === 2 ? 7 : 6, '0');
    this.playerEl.textContent = state?.activePlayer === 2 ? '2UP' : '1UP';
    this.stageEl.textContent = `STAGE ${state?.round ?? 1}`;
    this.livesEl.textContent = `FIGHTERS ${String(state?.players[state.activePlayer].lives ?? 3).padStart(2, '0')}`;
    this.status.textContent = this.statusMessage;
    this.root.querySelector<HTMLElement>('#mode-display')!.textContent = this.mode === 'single-player' ? 'SINGLE PLAYER' : 'ALTERNATING 2P';
    const badges = this.root.querySelector<HTMLElement>('#badge-display')!;
    badges.innerHTML = stageBadgeDenominations(state?.round ?? 1).map((badge) => `<span>${badge}</span>`).join('');
  }

  private announceState(): void {
    if (!this.state) return;
    const event = this.state.events.at(-1);
    const labels: Record<string, string> = { 'stage-clear': 'Stage clear', 'turn-change': `Player ${this.state.activePlayer} turn`, 'game-over': 'Game over', 'arcade-boundary': 'Arcade stage boundary' };
    const message = event ? labels[event.type] : undefined;
    if (message) { this.statusMessage = message.toUpperCase(); this.announcement.textContent = message; }
  }

  private openModePanel(): void {
    this.audio.activate();
    this.audio.startAttractMusic();
    this.showPanel(`<div class="panel-card"><button class="close" data-close aria-label="Close">×</button><div class="eyebrow">START SEQUENCE</div><h2>CHOOSE YOUR RUN</h2><p>Both modes use the original alternating turn order. Choose a mode to insert your web credit.</p><div class="panel-actions"><button class="primary" data-mode="single-player">1 PLAYER <small>SOLO FLIGHT</small></button><button data-mode="alternating-two-player">2 PLAYERS <small>TAKE TURNS</small></button></div><p class="microcopy">Leaderboard runs require an online server challenge. Offline play stays local.</p></div>`);
    this.panel.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((button) => button.addEventListener('click', () => { this.mode = button.dataset.mode as GameMode; this.startGame(); }));
  }

  private startGame(): void {
    this.closePanel();
    this.audio.stopAttractMusic();
    const requestedSeed = import.meta.env.DEV ? Number(new URLSearchParams(window.location.search).get('e2eSeed')) : NaN;
    const seed = Number.isSafeInteger(requestedSeed) && requestedSeed >= 0
      ? requestedSeed >>> 0
      : (crypto.getRandomValues(new Uint32Array(1))[0] ?? Date.now()) >>> 0;
    this.state = createGameState(seed, this.mode);
    this.demoState = null;
    this.onlineEligible = false;
    this.onlineRunId = null;
    this.onlineTokens = {};
    this.onlineSequences = { 1: 0, 2: 0 };
    this.pendingOnlineEvents = { 1: [], 2: [] };
    this.pendingTransition = null;
    this.statusMessage = 'FORMATION INBOUND';
    this.canvas.focus();
    this.audio.cue('stage-start');
    this.announceState();
    if (WORKER_URL && navigator.onLine) void this.prepareOnlineRun();
  }

  private async prepareOnlineRun(): Promise<void> {
    try {
      const response = await fetch(`${WORKER_URL.replace(/\/$/, '')}/runs`, { method: 'POST', headers: { 'content-type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ mode: this.mode }) });
      if (!response.ok) throw new Error('challenge unavailable');
      const data = await response.json() as { runId?: string; tokens?: { 1?: string; 2?: string } };
      if (!data.runId || !data.tokens?.[1]) throw new Error('challenge unavailable');
      this.onlineRunId = data.runId;
      this.onlineTokens = { 1: data.tokens[1], ...(data.tokens[2] ? { 2: data.tokens[2] } : {}) };
      this.onlineEligible = true;
      this.statusMessage = 'VALIDATED RUN // FORMATION INBOUND';
    } catch {
      this.onlineEligible = false;
      this.statusMessage = 'LOCAL RUN // NETWORK CHALLENGE UNAVAILABLE';
    }
  }

  private queueOnlineEvent(slot: 1 | 2, inputBits: number, transition: 'none' | 'pause' | 'resume', tick: number): void {
    if (!this.onlineEligible || !this.onlineRunId || !this.onlineTokens[slot]) return;
    const event: InputEvent = { sequence: ++this.onlineSequences[slot], tick, playerSlot: slot, inputBits, transition };
    this.pendingOnlineEvents[slot].push(event);
    if (this.pendingOnlineEvents[slot].length >= 16) void this.flushOnlineEvents(slot, false);
  }

  private async flushOnlineEvents(slot: 1 | 2, force: boolean): Promise<boolean> {
    if (this.onlineUploading[slot]) {
      await new Promise((resolve) => window.setTimeout(resolve, 25));
      return this.flushOnlineEvents(slot, force);
    }
    if (!this.onlineRunId || !this.onlineTokens[slot]) return true;
    if (!force && this.pendingOnlineEvents[slot].length < 16) return true;
    const events = this.pendingOnlineEvents[slot].slice(0, 32);
    if (!events.length) return true;
    this.onlineUploading[slot] = true;
    try {
      const response = await fetch(`${WORKER_URL.replace(/\/$/, '')}/runs/${this.onlineRunId}/events`, { method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${this.onlineTokens[slot]}` }, body: JSON.stringify(events) });
      if (!response.ok) { this.onlineEligible = false; return false; }
      this.pendingOnlineEvents[slot].splice(0, events.length);
      return true;
    } catch { this.onlineEligible = false; return false; }
    finally { this.onlineUploading[slot] = false; }
  }

  private togglePause(): void {
    if (!this.state || this.state.phase === 'game-over' || this.state.phase === 'arcade-boundary') return;
    const pausing = this.state.phase !== 'paused';
    setPaused(this.state, pausing);
    this.pendingTransition = pausing ? 'pause' : 'resume';
    if (pausing) { this.onlineEligible = false; this.statusMessage = 'PAUSED // LOCAL RUN'; }
    else this.statusMessage = 'FORMATION INBOUND';
  }

  private pauseForVisibility(): void { if (this.state?.phase === 'playing') { setPaused(this.state, true); this.onlineEligible = false; this.statusMessage = 'PAUSED // RETURNED TO TAB'; } }

  private toggleSound(): void {
    this.preferences.muted = !this.preferences.muted;
    this.audio.activate(); this.audio.updateVolume(); this.persistPreferences();
    this.root.querySelector<HTMLButtonElement>('#sound-button')!.textContent = this.preferences.muted ? 'SOUND OFF' : 'SOUND ON';
  }

  private persistPreferences(): void { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.preferences)); }

  private openInstructions(): void {
    this.showPanel(`<div class="panel-card reading"><button class="close" data-close aria-label="Close">×</button><div class="eyebrow">FIELD MANUAL</div><h2>HOW TO PLAY</h2><div class="instruction-grid"><div><strong>MOVE</strong><span>Arrow keys, A/D, or drag the pilot bar.</span></div><div><strong>FIRE</strong><span>Space or FIRE. Only two shots can be in flight.</span></div><div><strong>CAPTURE</strong><span>Boss Galaga beams cost a fighter. Destroy the captor while diving to rescue a dual fighter.</span></div><div><strong>CHALLENGE</strong><span>Stages 3, 7, 11, 15 and every fourth stage after are no-fire bonus routes.</span></div></div><p class="microcopy">Original vector art and synthesized audio are used. No ROM, ripped sprite, music, or sound asset is bundled.</p></div>`);
  }

  private openScores(): void {
    const rows = this.highScores.length ? this.highScores.map((score, index) => `<li><b>${String(index + 1).padStart(2, '0')}</b><strong>${escapeHtml(score.name)}</strong><span>${score.score.toLocaleString()}</span><small>STAGE ${score.stage}</small></li>`).join('') : '<li class="empty">NO LOCAL RUNS YET</li>';
    this.showPanel(`<div class="panel-card reading"><button class="close" data-close aria-label="Close">×</button><div class="eyebrow">CABINET MEMORY</div><h2>LOCAL SCORES</h2><ol class="score-list">${rows}</ol><p class="microcopy">Stored only on this device. Online submissions are validated by the Worker.</p></div>`);
  }

  private async openLeaderboard(): Promise<void> {
    this.showPanel('<div class="panel-card reading"><button class="close" data-close aria-label="Close">×</button><div class="eyebrow">NETWORK SIGNAL</div><h2>LEADERBOARD</h2><p id="board-loading">CONTACTING THE ARCADE...</p></div>');
    if (!WORKER_URL) { this.panel.querySelector('#board-loading')!.textContent = 'NETWORK BOARD IS NOT CONFIGURED. LOCAL SCORES ARE AVAILABLE OFFLINE.'; return; }
    try {
      const response = await fetch(`${WORKER_URL.replace(/\/$/, '')}/leaderboard?limit=20`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('unavailable');
      const data = await response.json() as { rows?: OnlineScore[] };
      const rows = data.rows?.map((score) => `<li><b>${String(score.rank).padStart(2, '0')}</b><strong>${escapeHtml(score.displayName)}</strong><span>${score.score.toLocaleString()}</span><small>STAGE ${score.stage}</small></li>`).join('') || '<li class="empty">NO SCORES YET</li>';
      const target = this.panel.querySelector('#board-loading');
      if (target) target.outerHTML = `<ol class="score-list">${rows}</ol>`;
    } catch { const target = this.panel.querySelector('#board-loading'); if (target) target.textContent = 'LEADERBOARD OFFLINE. YOUR RUN CAN STILL BE SAVED LOCALLY.'; }
  }

  private openGameOver(): void {
    const state = this.state!;
    const score = state.players[state.activePlayer].score;
    const eligible = score > 0 && isLeaderboardEligible(this.state, this.onlineEligible);
    this.showPanel(`<div class="panel-card"><div class="eyebrow">RUN COMPLETE</div><h2>GAME OVER</h2><div class="final-score">${score.toLocaleString()}<small>POINTS // STAGE ${state.round}</small></div><label class="initials-label" for="initials">ENTER INITIALS</label><input id="initials" maxlength="3" pattern="[A-Za-z0-9 _-]{1,3}" value="AAA" autocomplete="off"/><p class="microcopy">${eligible ? 'This run may be sent to the validated network board.' : 'Offline or paused runs are local-only.'}</p><div class="panel-actions"><button class="primary" data-save-score>SAVE SCORE</button><button data-close>RETURN TO ATTRACT</button></div></div>`);
    const initials = this.panel.querySelector<HTMLInputElement>('#initials')!;
    initials.focus();
    initials.addEventListener('input', () => this.audio.cue('high-score'));
    this.panel.querySelector<HTMLButtonElement>('[data-save-score]')!.addEventListener('click', () => void this.saveScore(eligible));
  }

  private openArcadeBoundary(): void {
    this.showPanel('<div class="panel-card"><div class="eyebrow">ARCADE AUTHENTIC BOUNDARY</div><h2>STAGE 0</h2><p>Stage 255 reached the original unsigned-counter boundary. This local run is not eligible for the online board.</p><button class="primary" data-close>RETURN TO ATTRACT</button></div>');
  }

  private async saveScore(tryOnline: boolean): Promise<void> {
    if (!this.state) return;
    const input = this.panel.querySelector<HTMLInputElement>('#initials')!;
    const normalized = normalizeDisplayName(input.value.slice(0, 3));
    if (!normalized.ok || !normalized.value) { input.setCustomValidity('Use 1-3 letters, numbers, spaces, hyphens, or underscores.'); input.reportValidity(); return; }
    const current = this.state.players[this.state.activePlayer];
    const local: LocalScore = { name: normalized.value, score: current.score, stage: this.state.round, createdAt: new Date().toISOString() };
    this.highScores = [...this.highScores, local].sort((left, right) => right.score - left.score).slice(0, 10);
    localStorage.setItem(HIGH_SCORE_KEY, JSON.stringify(this.highScores));
    let networkMessage = 'SAVED TO THIS CABINET.';
    if (tryOnline && WORKER_URL) {
      networkMessage = await this.submitOnline(normalized.value) ? 'VALIDATED BY THE NETWORK ARCADE.' : 'NETWORK REJECTED THE RUN; LOCAL COPY KEPT.';
    }
    this.showPanel(`<div class="panel-card"><div class="eyebrow">SCORE REGISTERED</div><h2>${escapeHtml(normalized.value)}</h2><div class="final-score">${current.score.toLocaleString()}<small>${networkMessage}</small></div><button class="primary" data-close>RETURN TO ATTRACT</button></div>`);
  }

  private async submitOnline(displayName: string): Promise<boolean> {
    if (!this.onlineRunId || !this.onlineEligible || !displayName) return false;
    for (const slot of [1, 2] as const) {
      while (this.pendingOnlineEvents[slot].length > 0) {
        if (!(await this.flushOnlineEvents(slot, true))) return false;
      }
    }
    const slots = this.mode === 'alternating-two-player' ? [1, 2] as const : [1] as const;
    for (const slot of slots) {
      const token = this.onlineTokens[slot];
      if (!token) continue;
      try {
        const response = await fetch(`${WORKER_URL.replace(/\/$/, '')}/runs/${this.onlineRunId}/submit`, { method: 'POST', headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ displayName }) });
        if (!response.ok) return false;
      } catch { return false; }
    }
    return true;
  }

  private showPanel(content: string): void {
    this.panel.innerHTML = content;
    this.panel.hidden = false;
    this.panel.querySelectorAll<HTMLElement>('[data-close]').forEach((element) => element.addEventListener('click', () => this.closePanel()));
    this.panel.addEventListener('click', (event) => { if (event.target === this.panel) this.closePanel(); }, { once: true });
  }

  private closePanel(): void {
    const terminal = this.state?.phase === 'game-over' || this.state?.phase === 'arcade-boundary';
    this.panel.hidden = true;
    this.panel.innerHTML = '';
    if (terminal) this.returnToAttract();
  }

  private returnToAttract(): void {
    this.state = null;
    this.demoState = null;
    this.onlineRunId = null;
    this.onlineTokens = {};
    this.onlineEligible = false;
    this.attractTick = 0;
    this.statusMessage = 'READY FOR INSERT COIN';
  }

  private setOnlineStatus(online: boolean): void {
    this.onlineEl.textContent = online && WORKER_URL ? 'NETWORK READY' : 'LOCAL READY';
    this.root.querySelector<HTMLElement>('.status-dot')!.classList.toggle('online', online && Boolean(WORKER_URL));
    this.root.querySelector<HTMLElement>('#connection-footer')!.textContent = online ? 'ONLINE' : 'OFFLINE OK';
  }

  public testReset(seed = 1, mode: GameMode = 'single-player'): void {
    this.closePanel();
    this.state = createGameState(seed, mode);
    this.demoState = null;
    this.onlineEligible = false;
    this.onlineRunId = null;
    this.onlineTokens = {};
    this.statusMessage = 'TEST FLIGHT';
    this.render(performance.now());
  }

  public testScenario(name: BrowserTestScenario): void {
    this.testReset(0x1981);
    const state = this.state!;
    const playerX = state.playerX;
    if (name === 'boss' || name === 'capture' || name === 'formation-captor') {
      const boss = state.enemies.find((enemy) => enemy.kind === 'boss')!;
      boss.x = playerX;
      boss.y = 300;
      boss.active = true;
      boss.hp = name === 'boss' ? 2 : 1;
      boss.phase = name === 'formation-captor' ? 'formation' : 'attack';
      boss.escorts = 2;
      if (name === 'capture') startTractorBeam(state, boss.id);
      if (name === 'formation-captor') state.players[1].capture = 'pulling';
    } else if (name === 'dual') {
      state.players[1].fighterCount = 2;
      state.enemyShots = [{ x: playerX, y: 358, vx: 0, vy: 0, owner: 'enemy' }];
    } else if (name === 'morph') {
      state.formationReady = true;
      state.enemies = [0, 1, 2].map((id) => ({ id, kind: 'morph' as const, morphKind: 'scorpion' as const, x: playerX, y: 300, formationX: playerX, formationY: 300, phase: 'formation' as const, hp: 1 as const, damaged: false, active: true, escorts: 0, attackTicks: 0 }));
    } else if (name === 'challenge') {
      state.round = 3;
      state.descriptor = createRoundDescriptor(3);
      state.enemies = state.enemies.map((enemy, id) => ({ ...enemy, id, kind: 'challenge' as const, phase: 'attack' as const, active: true, challengeTarget: state.descriptor.challengeRoutes[id]?.target ?? 'dragonfly' }));
      state.challengeTotal = 40;
    } else if (name === 'stage-clear') {
      state.enemies = [];
      state.formationReady = true;
    } else if (name === 'game-over') {
      state.phase = 'game-over';
      this.render(performance.now());
      this.openGameOver();
    }
    this.render(performance.now());
  }

  public testStep(inputBits: number = InputBits.NONE, ticks = 1): void {
    if (!this.state) this.testReset();
    for (let tick = 0; tick < Math.max(1, Math.min(600, ticks)); tick++) step(this.state!, inputBits);
    this.render(performance.now());
  }

  public testCompleteChallenge(): void {
    if (!this.state) return;
    for (const enemy of this.state.enemies) if (enemy.phase !== 'destroyed') destroyEnemy(this.state, enemy.id);
    step(this.state, InputBits.NONE);
    this.render(performance.now());
  }

  public testSpawnEnemyShot(): void {
    if (!this.state) return;
    this.state.enemyShots.push({ x: this.state.playerX, y: 358, vx: 0, vy: 0, owner: 'enemy' });
    this.render(performance.now());
  }

  public testState(): ReturnType<BrowserTestBridge['state']> {
    if (!this.state) return null;
    const current = this.state.players[this.state.activePlayer];
    return { seed: this.state.seed, tick: this.state.tick, phase: this.state.phase, round: this.state.round, score: current.score, lives: current.lives, fighterCount: current.fighterCount, capture: current.capture, playerX: this.state.playerX, shots: this.state.shots.length, enemyShots: this.state.enemyShots.length, explosions: this.state.explosions.length, enemyCount: this.state.enemies.filter((enemy) => enemy.phase !== 'destroyed').length, challengeDestroyed: this.state.challengeDestroyed, bossDamaged: this.state.enemies.some((enemy) => enemy.kind === 'boss' && enemy.damaged) };
  }
}

const root = document.querySelector<HTMLElement>('#app');
if (root) {
  const game = new GameApp(root);
  if (import.meta.env.DEV) {
    window.__STARFALL_TEST__ = {
      reset: (seed, mode) => game.testReset(seed, mode),
      scenario: (name) => game.testScenario(name),
      step: (inputBits, ticks) => game.testStep(inputBits, ticks),
      completeChallenge: () => game.testCompleteChallenge(),
      spawnEnemyShot: () => game.testSpawnEnemyShot(),
      state: () => game.testState(),
    };
  }
}
